import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

const SIGNUP_TRACKED_KEY = 'coachsim-signup-tracked';

/**
 * Watches Supabase auth state changes and fires `signup_completed`
 * when a newly created user signs in for the first time.
 *
 * Covers both email-verified and Google OAuth signups with one listener.
 * Uses localStorage to prevent double-firing across page reloads.
 *
 * Renders nothing — mount anywhere inside the React tree.
 */
export function AuthListener() {
  const trackedRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event !== 'SIGNED_IN' || !session?.user || trackedRef.current) return;

        const user = session.user;
        const createdAt = new Date(user.created_at).getTime();
        const now = Date.now();
        // Treat as new signup if account was created within the last 5 minutes
        const isNewUser = now - createdAt < 5 * 60 * 1000;

        if (!isNewUser) return;

        // Deduplicate: don't fire again if we already tracked this user ID
        try {
          if (localStorage.getItem(SIGNUP_TRACKED_KEY) === user.id) return;
        } catch {
          // Private browsing — proceed but accept possible duplicate
        }

        const provider = (user.app_metadata?.provider as string) || 'email';
        trackEvent('signup_completed', { method: provider });

        try {
          localStorage.setItem(SIGNUP_TRACKED_KEY, user.id);
        } catch {
          // no-op
        }
        trackedRef.current = true;
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return null;
}
